import React from "react";

/**
 * Subtle animated golden particle / cosmic-dust overlay for the hero.
 * Pure SVG + CSS; no runtime cost.
 * Sits above the photo but below the hero text.
 */
export default function HeroGoldOverlay({ density = "medium" }) {
  const count = density === "high" ? 22 : density === "low" ? 8 : 14;
  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden="true"
      data-testid="hero-gold-overlay"
    >
      {/* Soft radial vignette */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.06)_0%,transparent_55%)]" />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1000 600" preserveAspectRatio="xMidYMid slice">
        <defs>
          <radialGradient id="grain" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#F2D88A" stopOpacity="0.95" />
            <stop offset="60%" stopColor="#D4AF37" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="largeGlow" cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#F2D88A" stopOpacity="0.20" />
            <stop offset="100%" stopColor="#0A0A0A" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Large warm glow behind the hero copy */}
        <ellipse cx="650" cy="220" rx="320" ry="200" fill="url(#largeGlow)" />

        {/* Animated golden particles */}
        {Array.from({ length: count }).map((_, i) => {
          // Deterministic-ish positions
          const cx = ((i * 137) % 1000);
          const cy = ((i * 211 + 80) % 600);
          const r = 1.5 + ((i * 7) % 9) / 4;
          const dur = 6 + (i % 6);
          const delay = (i * 0.4) % 4;
          return (
            <g key={i} style={{ transformOrigin: `${cx}px ${cy}px` }}>
              <circle cx={cx} cy={cy} r={r} fill="url(#grain)">
                <animate
                  attributeName="opacity"
                  values="0;1;0"
                  dur={`${dur}s`}
                  begin={`${delay}s`}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="cy"
                  values={`${cy};${cy - 30};${cy}`}
                  dur={`${dur}s`}
                  begin={`${delay}s`}
                  repeatCount="indefinite"
                />
              </circle>
            </g>
          );
        })}

        {/* Three thin orbit rings — DNA / cosmic motif */}
        <g opacity="0.18" stroke="#D4AF37" strokeWidth="0.6" fill="none">
          <ellipse cx="640" cy="240" rx="280" ry="40" />
          <ellipse cx="640" cy="240" rx="280" ry="40" transform="rotate(30 640 240)" />
          <ellipse cx="640" cy="240" rx="280" ry="40" transform="rotate(-30 640 240)" />
        </g>
      </svg>
    </div>
  );
}
